// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, xScreen } from "@stanion/XDL/Utilities";
import { css } from "styled-components";

interface MyAccountFilterSectionStyles {
    /** GridContainer for countGridItem and toggleFilterGridItem */
    filterContainer?: GridContainerProps;
    /** Count orders GridItem */
    countGridItem?: GridItemProps;
    /** Filter clickable GridItem */
    toggleFilterGridItem?: GridItemProps;
    /** Toggle filter icon */
    toggleFilterIcon?: IconPresentationProps;
    /** Toggle filter clickable */
    toggleFilterClickable?: ClickablePresentationProps;
    searchFormStyles: {
        wrapper?: InjectableCss;
        headingText?: TypographyPresentationProps;
    };
    clearButton?: ButtonPresentationProps;
    toggleFilterButton?: ButtonPresentationProps;
}

const myAccountFilterSectionStyles: MyAccountFilterSectionStyles = {
    filterContainer: {
        gap: 8,
        css: css`
            margin-bottom: 0;
        `,
    },
    countGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            font-size: 14px;
            font-weight: bold;
            align-items: center;
            text-transform: capitalize;
            order: 2;

            &,
            & > div {
                text-align: right;
                justify-content: flex-end;
            }

            ${xScreen("lte", "xs")} {
                order: 1;
                &,
                & > div {
                    text-align: left;
                    justify-content: flex-start;
                }
            }
        `,
    },
    toggleFilterGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            order: 1;
            ${xScreen("lte", "xs")} {
                order: 2;
            }
        `,
    },
    toggleFilterClickable: {
        css: css`
            min-width: 120px;
            height: 55px;
            text-align: center;
            padding: 10px 15px;
            border: 1px solid ${color("common.border")};
            border-top: 5px solid ${color("primary")};
            justify-content: center;
            font-size: 18px;
            background: ${color("common.white")};
            margin: 0px;
            color: ${color("text.main")};

            &:after {
                content: "${translate("Filter")}";
                font-weight: bold;
            }

            &:hover:after {
                color: ${color("primary")};
                outline: none;
            }

            &:focus {
                outline: none;
            }
        `,
    },
    toggleFilterButton: {
        variant: "secondary",
        css: css`
            min-width: 120px;
            height: 55px;
            text-align: center;
            padding: 10px 15px;
            border: 1px solid ${color("common.border")};
            border-top: 5px solid ${color("primary")};
            justify-content: center;
            font-size: 18px;
            background: ${color("common.white")};
            margin: 0px;
            color: ${color("primary")} !important;

            &:after {
                content: "${translate("Filter")}";
                font-weight: bold;
            }

            &:hover:after {
                color: ${color("primary")};
                outline: none;
            }

            &:focus,
            &:hover {
                color: ${color("primary")} !important;
                background: ${color("common.white")} !important;
                outline: none;
                &&&& {
                    border-top-color: ${color("primary")};
                    border-left-color: ${color("common.border")};
                    border-right-color: ${color("common.border")};
                    border-bottom-color: ${color("common.border")};
                }
            }
        `,
    },
    toggleFilterIcon: {
        src: Filter,
        size: 15,
        css: css`
            display: none;
        `,
    },
    searchFormStyles: {
        wrapper: {
            css: css`
                margin-bottom: 40px;
                background: ${color("common.white")};
                border: 1px solid ${color("common.border")};
                padding: 10px 15px;
                margin-top: -25px;
                position: relative;
                &:before {
                    content: " ";
                    width: 118px;
                    height: 1px;
                    position: absolute;
                    top: -1px;
                    background: white;
                    left: 0;
                }
            `,
        },
        headingText: {
            variant: "h5",
            css: css`
                display: none;
            `,
        },
    },
    clearButton: {
        variant: "secondary",
        css: css`
            float: right;
            margin-bottom: 15px;
            margin-top: 5px;
        `,
        mergeCss: true,
    },
};

export default myAccountFilterSectionStyles;
